import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import { METRIC_STATS_GRID_CLASS } from "../../../shared/components/MetricStatCard";
import Button from "../../../shared/ui/Button";
import {
  fetchAdminUserDetail,
  impersonateAdminUser,
  extendAdminUserTrial,
  formatAdminError,
} from "../adminApi";
import AdminUserActivityTimeline from "../components/AdminUserActivityTimeline";
import AdminUserOfferCard from "../components/AdminUserOfferCard";
import {
  applyAuthResponse,
  getStoredIsSuperuser,
} from "../../../lib/authSession";
import { getPostLoginPath } from "../../../lib/authRoutes";
import { applyAccountStatusToSession } from "../../../lib/accountStatus";
import { accountStatusLabel } from "../../../lib/accountStatus";
import { useTranslation } from "../../../i18n";

const TRIAL_PRESETS = [3, 7, 14, 30];

function formatDateLocale(iso, locale) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale === "de" ? "de-DE" : "en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTimeLocale(iso, locale) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(locale === "de" ? "de-DE" : "en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function daysRemaining(iso) {
  if (!iso) return null;
  const end = new Date(iso);
  if (Number.isNaN(end.getTime())) return null;
  const ms = end.getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

const AdminUserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t, locale } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [impersonating, setImpersonating] = useState(false);
  const [trialDays, setTrialDays] = useState(7);
  const [extending, setExtending] = useState(false);

  const loadUser = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminUserDetail(userId);
      setUser(data);
    } catch (err) {
      setError(formatAdminError(err));
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const handleImpersonate = async () => {
    if (!user || user.isSuperuser) return;
    setImpersonating(true);
    setError("");
    try {
      const data = await impersonateAdminUser(user.id);
      applyAuthResponse(data);
      if (data?.user) applyAccountStatusToSession(data.user);
      // Prefetch this client's connected platforms before landing in their dashboard.
      const { hydrateConnectionsFromServer } = await import(
        "../../../client/dashboard/platformConnectionsStorage"
      );
      await hydrateConnectionsFromServer(data?.user?.company?.id);
      navigate(getPostLoginPath(data?.user?.role || "user"), { replace: true });
    } catch (err) {
      setError(formatAdminError(err));
    } finally {
      setImpersonating(false);
    }
  };

  const handleExtendTrial = async () => {
    if (!user || user.role !== "user" || extending) return;
    const days = Number(trialDays);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      setError(t("adminUserDetail.daysLabel"));
      return;
    }
    setExtending(true);
    setError("");
    setSuccess("");
    try {
      const updated = await extendAdminUserTrial(user.id, days);
      setUser(updated);
      setSuccess(t("adminUserDetail.extendSuccess"));
      window.setTimeout(() => setSuccess(""), 3500);
    } catch (err) {
      setError(formatAdminError(err));
    } finally {
      setExtending(false);
    }
  };

  const canImpersonate =
    user &&
    !user.isSuperuser &&
    (user.role === "user" || (user.role === "admin" && getStoredIsSuperuser()));

  const trialInfo = useMemo(() => {
    if (!user?.trialEndsAt) {
      return { label: t("adminUserDetail.trialNotSet"), remaining: null, active: false };
    }
    const remaining = daysRemaining(user.trialEndsAt);
    if (remaining != null && remaining > 0) {
      return {
        label: t("adminUserDetail.trialEnds", {
          date: formatDateTimeLocale(user.trialEndsAt, locale),
        }),
        remaining,
        active: true,
      };
    }
    return {
      label: t("adminUserDetail.trialExpiredOn", {
        date: formatDateTimeLocale(user.trialEndsAt, locale),
      }),
      remaining: remaining ?? 0,
      active: false,
    };
  }, [user, t, locale]);

  const showTrialControls =
    user &&
    user.role === "user" &&
    !user.isSuperuser &&
    !user.isSubUser;

  const teamMembers = user?.teamMembers || [];

  return (
    <div className="bg-background font-body-md text-on-surface">
      <div className="mx-auto max-w-container-max px-margin-mobile py-stack-lg md:px-margin-desktop">
        <nav className="mb-stack-lg">
          <Link
            to="/dashboard/admin/users"
            className="group inline-flex items-center gap-2 transition-colors"
          >
            <MaterialIcon
              name="arrow_back"
              className="text-[20px] text-outline transition-colors group-hover:text-primary"
            />
            <span className="font-label-sm text-outline transition-colors group-hover:text-primary">
              {t("adminUserDetail.backToUsers")}
            </span>
          </Link>
        </nav>

        {loading ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-outline-variant/30 bg-surface-container-lowest py-20">
            <MaterialIcon name="sync" className="animate-spin text-[36px] text-secondary" />
            <p className="mt-4 text-body-md text-on-surface-variant">{t("admin.loadingUsers")}</p>
          </div>
        ) : !user ? (
          <div className="tonal-card rounded-2xl px-6 py-14 text-center">
            <p className="font-headline-md text-primary">{t("adminUserDetail.notFound")}</p>
            {error ? <p className="mt-2 text-body-md text-error">{error}</p> : null}
          </div>
        ) : (
          <>
            <div className="mb-stack-lg flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-headline-lg text-headline-lg text-primary">{user.fullName}</h1>
                  {user.isSuperuser ? (
                    <span className="rounded bg-primary px-2 py-0.5 text-[10px] font-bold uppercase text-on-primary">
                      {t("adminTable.superLabel")}
                    </span>
                  ) : null}
                  {user.isSubUser ? (
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-700 ring-1 ring-inset ring-slate-200">
                      {t("adminUserDetail.teamMemberBadge")}
                    </span>
                  ) : null}
                  {user.isAccountOwner && user.role === "user" ? (
                    <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-200">
                      {t("adminUserDetail.ownerBadge")}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-body-md text-on-surface-variant">{user.email}</p>
                {user.isSubUser && user.ownerId ? (
                  <p className="mt-1 text-body-md text-on-surface-variant">
                    {t("adminUserDetail.ownerOf", {
                      name: user.ownerName || user.ownerEmail || `#${user.ownerId}`,
                    })}{" "}
                    <Link
                      to={`/dashboard/admin/users/${user.ownerId}`}
                      className="font-semibold text-secondary hover:underline"
                    >
                      {t("adminUserDetail.viewOwner")}
                    </Link>
                  </p>
                ) : null}
                <p className="mt-1 text-label-sm text-on-surface-variant">
                  {t("adminUserDetail.registered", {
                    date: formatDateLocale(user.dateJoined, locale),
                  })}
                </p>
              </div>
              {canImpersonate ? (
                <Button
                  type="button"
                  variant="teal"
                  className="!h-11 !w-auto shrink-0 !rounded-full !px-6"
                  disabled={impersonating}
                  onClick={handleImpersonate}
                >
                  {impersonating
                    ? t("admin.loggingIn")
                    : user.role === "user"
                      ? t("admin.openClientProfile")
                      : t("admin.loginAsAdmin")}
                </Button>
              ) : null}
            </div>

            {error ? (
              <p className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-body-md text-on-error-container">
                {error}
              </p>
            ) : null}
            {success ? (
              <p className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-body-md text-emerald-900">
                {success}
              </p>
            ) : null}

            <div className={METRIC_STATS_GRID_CLASS}>
              {[
                {
                  label: t("admin.role"),
                  value:
                    user.role === "admin" ? t("admin.administrator") : t("admin.userRole"),
                  icon: "badge",
                },
                {
                  label: t("admin.account"),
                  value:
                    user.role === "user"
                      ? accountStatusLabel(user.accountStatus, t)
                      : "—",
                  icon: "payments",
                },
                {
                  label: t("adminUserDetail.cases"),
                  value: user.caseCount,
                  icon: "gavel",
                  to: "/dashboard/admin/deletion-cases",
                },
                {
                  label: t("adminUserDetail.reviews"),
                  value: user.reviewCount,
                  icon: "rate_review",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="tonal-card rounded-xl border border-outline-variant/30 p-stack-lg"
                >
                  <MaterialIcon name={item.icon} className="mb-2 text-secondary" />
                  <p className="font-label-caps text-on-surface-variant">{item.label}</p>
                  <p className="mt-1 text-2xl font-bold text-primary">{item.value}</p>
                  {item.to ? (
                    <Link
                      to={item.to}
                      className="mt-3 inline-flex items-center gap-1 text-label-sm font-medium text-secondary hover:underline"
                    >
                      {t("adminUserDetail.view")}
                      <MaterialIcon name="arrow_forward" className="text-[14px]" />
                    </Link>
                  ) : null}
                </div>
              ))}
            </div>

            {showTrialControls ? (
              <section className="tonal-card mt-stack-lg rounded-xl border border-outline-variant/30 p-stack-lg">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-headline-md text-primary">
                      {t("adminUserDetail.trialSection")}
                    </h2>
                    <p className="mt-1 text-body-md text-on-surface-variant">
                      {t("adminUserDetail.extendTrialHint")}
                    </p>
                  </div>
                  <span
                    className={[
                      "rounded-full px-3 py-1 text-label-sm font-semibold ring-1 ring-inset",
                      trialInfo.active
                        ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
                        : "bg-amber-50 text-amber-900 ring-amber-200",
                    ].join(" ")}
                  >
                    {trialInfo.active && trialInfo.remaining != null
                      ? t("adminUserDetail.trialDaysLeft", { count: trialInfo.remaining })
                      : accountStatusLabel(user.accountStatus, t)}
                  </span>
                </div>

                <p className="mt-4 text-body-md text-on-surface">{trialInfo.label}</p>

                <div className="mt-5">
                  <p className="mb-2 text-label-sm font-semibold text-on-surface-variant">
                    {t("adminUserDetail.daysLabel")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {TRIAL_PRESETS.map((days) => (
                      <button
                        key={days}
                        type="button"
                        onClick={() => setTrialDays(days)}
                        className={[
                          "rounded-full px-3 py-1.5 text-label-sm font-semibold transition",
                          Number(trialDays) === days
                            ? "bg-secondary text-on-secondary"
                            : "border border-outline-variant/40 bg-white text-on-surface-variant hover:border-secondary/40",
                        ].join(" ")}
                      >
                        +{days}
                      </button>
                    ))}
                    <label className="inline-flex items-center gap-2 rounded-full border border-outline-variant/40 bg-white px-3 py-1 text-label-sm text-on-surface-variant">
                      <span>{t("adminUserDetail.customDays")}</span>
                      <input
                        type="number"
                        min={1}
                        max={365}
                        value={trialDays}
                        onChange={(e) => setTrialDays(e.target.value)}
                        className="w-16 border-0 bg-transparent p-0 text-center font-semibold text-primary outline-none"
                      />
                    </label>
                  </div>
                </div>

                <div className="mt-5">
                  <Button
                    type="button"
                    variant="teal"
                    className="!h-11 !w-auto !rounded-full !px-6"
                    disabled={extending}
                    onClick={handleExtendTrial}
                  >
                    {extending
                      ? t("adminUserDetail.extending")
                      : t("adminUserDetail.extendButton", { days: trialDays })}
                  </Button>
                </div>
              </section>
            ) : null}

            {user.isAccountOwner && user.role === "user" && !user.isSuperuser ? (
              <AdminUserOfferCard userId={user.id} />
            ) : null}

            <div className="tonal-card mt-stack-lg rounded-xl border border-outline-variant/30 p-stack-lg">
              <h2 className="font-headline-md text-primary">{t("adminTable.company")}</h2>
              <p className="mt-2 text-body-md text-on-surface">
                {user.companyName || t("adminUserDetail.noCompany")}
              </p>
            </div>

            {user.isAccountOwner && user.role === "user" ? (
              <section className="tonal-card mt-stack-lg rounded-xl border border-outline-variant/30 p-stack-lg">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-headline-md text-primary">
                      {t("adminUserDetail.teamSection")}
                    </h2>
                    <p className="mt-1 text-body-md text-on-surface-variant">
                      {t("adminUserDetail.teamSectionHint")}
                    </p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-label-sm font-semibold text-slate-700 ring-1 ring-inset ring-slate-200">
                    {t("adminUserDetail.membersCount", {
                      count: teamMembers.length,
                    })}
                  </span>
                </div>

                {teamMembers.length === 0 ? (
                  <p className="mt-5 text-body-md text-on-surface-variant">
                    {t("adminUserDetail.teamEmpty")}
                  </p>
                ) : (
                  <ul className="mt-5 divide-y divide-outline-variant/20">
                    {teamMembers.map((member) => (
                      <li
                        key={member.id}
                        className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate font-semibold text-primary">
                              {member.fullName || member.email}
                            </p>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                              {t("adminUserDetail.teamMemberBadge")}
                            </span>
                          </div>
                          <p className="truncate text-body-md text-on-surface-variant">
                            {member.email}
                          </p>
                          <p className="mt-0.5 text-label-sm text-on-surface-variant">
                            {t("adminUserDetail.joined", {
                              date: formatDateLocale(
                                member.joinedAt || member.dateJoined,
                                locale
                              ),
                            })}
                          </p>
                        </div>
                        <Link
                          to={`/dashboard/admin/users/${member.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-outline-variant/40 bg-white px-3 py-1.5 text-label-sm font-semibold text-secondary transition hover:border-secondary/40"
                        >
                          {t("adminUserDetail.openMember")}
                          <MaterialIcon name="arrow_forward" className="text-[14px]" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ) : null}

            <AdminUserActivityTimeline userId={user.id} />
          </>
        )}
      </div>
    </div>
  );
};

export default AdminUserDetailPage;
